import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import { purchaseAPI } from "../../services";
import AddPurchaseModal from "../purchases/AddPurchaseModal";
import EditPurchaseModal from "../purchases/EditPurchaseModal";
import PurchasesView from "../purchases/PurchasesView";

import EditIcon from "../../assets/icons/EditIcon";
import RedCircle from "../../assets/icons/RedCircle";
import GreenCircle from "../../assets/icons/GreenCircle";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import { useSelector } from "react-redux";
import "../../style/purchases.css";

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [viewingPurchase, setViewingPurchase] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPurchase, setDeletingPurchase] = useState(null);
  const [, setLoading] = useState(false);
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  // Load purchases from API
  const fetchPurchases = async (search = "", page = 1) => {
    setLoading(true);
    try {
      const response = await purchaseAPI.getPurchasesList({
        search,
        page,
        page_size: itemsPerPage,
      });

      if (response && Array.isArray(response.results)) {
        setPurchases(response.results);
        setTotalCount(response.count || 0);
      } else if (response && Array.isArray(response)) {
        setPurchases(response);
        setTotalCount(response.length);
      } else {
        setPurchases([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching purchases:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch purchases. Please try again.";
      toast.error(errorMessage);
      setPurchases([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    const timeoutId = setTimeout(() => {
      fetchPurchases(searchTerm, 1);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Add or update purchase
  const handleSave = async (purchase) => {
    try {
      setLoading(true);
      if (editingPurchase) {
        const response = await purchaseAPI.updatePurchase(
          editingPurchase.id,
          purchase
        );
        if (response) {
          toast.success("Purchase updated successfully!");
          await fetchPurchases();
          setShowEditModal(false);
          setEditingPurchase(null);
        }
      } else {
        const response = await purchaseAPI.createPurchase(purchase);
        if (response) {
          toast.success("Purchase created successfully!");
          await fetchPurchases();
          setShowAddModal(false);
        }
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to save purchase. Please try again.";
      toast.error(errorMessage);

      // Fallback to localStorage
      if (editingPurchase) {
        setPurchases((prev) =>
          prev.map((p) =>
            p.BatchId === editingPurchase.BatchId
              ? { ...purchase, BatchId: editingPurchase.BatchId }
              : p
          )
        );
        setShowEditModal(false);
        setEditingPurchase(null);
      } else {
        setPurchases((prev) => [
          ...prev,
          {
            ...purchase,
            BatchId: `BATCH-${Date.now()}`,
            date: new Date().toISOString(),
            status: "Active",
          },
        ]);
        setShowAddModal(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete purchase
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await purchaseAPI.deletePurchase(id);
      toast.success("Purchase deleted successfully!");
      await fetchPurchases();
      setShowDeleteModal(false);
      setDeletingPurchase(null);
    } catch (error) {
      console.error("Error deleting purchase:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete purchase. Please try again.";
      toast.error(errorMessage);

      // Fallback to localStorage
      setPurchases((prev) => prev.filter((p) => p.BatchId !== id));
      setShowDeleteModal(false);
      setDeletingPurchase(null);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const endIndex = Math.min(startIndex + itemsPerPage, totalCount);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPurchases(searchTerm, page);
  };

  return (
    <div className="purchases-container">
      {/* Header */}
      <div className="purchases-header">
        <div>
          <h1 className="purchases-title">Purchases</h1>
          <p className="purchases-subtitle">Manage your purchases</p>
        </div>
      </div>

      {/* Search */}
      <div className="purchases-search-container">
        <div className="position-relative">
          <input
            type="text"
            className={`${
              isPartsURL
                ? "purchases-search-input-parts"
                : isAccessoriesURL
                ? "purchases-search-input-acc"
                : isGodownURL
                ? "purchases-search-input-godown"
                : "purchases-search-input"
            }`}
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search size={16} className="purchases-search-icon" />
        </div>
      </div>

      {/* Table */}
      <div className="purchases-table-responsive">
        <table className="purchases-table">
          <thead>
            <tr>
              <th>BATCH ID</th>
              <th>VENDOR</th>
              <th>PRODUCT</th>
              <th>CATEGORY</th>
              <th>QTY.</th>
              <th>BUYING PRICE</th>
              <th>SELLING PRICE</th>
              <th>DATE</th>
              <th>STATUS</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 ? (
              <tr>
                <td colSpan="12" className="no-products-message">
                  <div
                    className=""
                    style={{
                      marginBottom: "4rem",
                      marginTop: "4rem",
                    }}
                  >
                    <NoDataIcon />
                    <h3 style={{ marginTop: "2rem" }}>No Purchases Found</h3>
                  </div>
                </td>
              </tr>
            ) : (
              purchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td>{purchase?.batch_number || "-"}</td>
                  <td>{purchase.vendor_name || "-"}</td>
                  <td className="product-name-cell">
                    <div>
                      <div>{purchase.product_name || "—"}</div>
                      <div
                        style={{
                          fontSize: "0.8em",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {purchase.product_detail?.ram &&
                          `${purchase.product_detail.ram}`}
                        {purchase.product_detail?.ram &&
                          purchase.product_detail?.storage &&
                          " | "}
                        {purchase.product_detail?.storage &&
                          `${purchase.product_detail.storage}`}
                        {purchase.product_detail?.variants &&
                          purchase.product_detail.variants.length > 0 && (
                            <>
                              {(purchase.product_detail?.ram ||
                                purchase.product_detail?.storage) &&
                                " | "}
                              {purchase.product_detail.variants
                                .map((variant) =>
                                  Object.values(variant).join(", ")
                                )
                                .join(", ")}
                            </>
                          )}
                        {(purchase.product_detail?.ram ||
                          purchase.product_detail?.storage ||
                          (purchase.product_detail?.variants &&
                            purchase.product_detail.variants.length > 0)) &&
                          (purchase.product_detail?.suffix ||
                            purchase.product_detail?.used_mobile !==
                              undefined) &&
                          " | "}
                        {purchase.product_detail?.suffix &&
                          `${purchase.product_detail.suffix}`}
                        {purchase.product_detail?.suffix &&
                          purchase.product_detail?.used_mobile !== undefined &&
                          " | "}
                        {purchase.product_detail?.used_mobile !== undefined &&
                          purchase.product_detail?.used_mobile !== null &&
                          (purchase.product_detail.used_mobile === false
                            ? "New"
                            : "Used")}
                      </div>
                    </div>
                  </td>
                  <td>{purchase.category_name || "-"}</td>
                  <td>{purchase?.available_qty || "0"}</td>
                  <td className="price-cell">
                    €{purchase.buying_price || "0.00"}
                  </td>
                  <td className="price-cell">
                    €{purchase.selling_price || "0.00"}
                  </td>
                  <td className="date-cell">
                    {purchase.created_at
                      ? new Date(purchase.created_at).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <span style={{ marginLeft: "1rem" }}>
                      {purchase.available_qty > 0 ? (
                        <GreenCircle />
                      ) : (
                        <RedCircle />
                      )}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className={`${
                          isPartsURL
                            ? "action-btn-parts view"
                            : isAccessoriesURL
                            ? "action-btn-acc view"
                            : isGodownURL
                            ? "action-btn-godown view"
                            : "action-btn view"
                        }`}
                        onClick={() => {
                          setEditingPurchase(purchase);
                          setShowEditModal(true);
                        }}
                      >
                        <EditIcon
                          fill={
                            isPartsURL
                              ? "var(--primary-color-parts)"
                              : isAccessoriesURL
                              ? "var(--primary-color-acc)"
                              : isGodownURL
                              ? "var(--primary-color-godown)"
                              : "var(--primary-color-mobile)"
                          }
                        />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => {
                          setDeletingPurchase(purchase);
                          setShowDeleteModal(true);
                        }}
                      >
                        <RedDeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - Only show when there are more items than itemsPerPage */}
      {totalCount > itemsPerPage && (
        <div className="pagination-container">
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>

          <div className="pagination-buttons">
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-acc"
                  : isGodownURL
                  ? "pagination-btn-godown"
                  : "pagination-btn"
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-next-acc"
                  : isGodownURL
                  ? "pagination-btn-next-godown"
                  : "pagination-btn-next"
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddPurchaseModal
          onSave={handleSave}
          onCancel={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && (
        <EditPurchaseModal
          purchase={editingPurchase}
          onSave={handleSave}
          onCancel={() => {
            setShowEditModal(false);
            setEditingPurchase(null);
          }}
        />
      )}

      {viewingPurchase && (
        <PurchasesView
          purchase={viewingPurchase}
          onClose={() => setViewingPurchase(null)}
        />
      )}

      {/* delete modal */}
      {showDeleteModal && deletingPurchase && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Purchase</h3>
              <p className="modal-message">
                Are you sure you want to delete purchase{" "}
                <b>{deletingPurchase.unique_id || deletingPurchase.id}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDelete(deletingPurchase.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
